'use client'

import * as React from 'react';
import { useNovaStoreContext } from './nova-store-context';

/**
 * PanelWidthObserver Component
 * 
 * This component measures the width of a specific Nova panel and updates the store.
 * It should be placed at the root of each panel.
 */
export function PanelWidthObserver({ 
  children, 
  panel 
}: { 
  children: React.ReactNode;
  panel: 'datasets' | 'center' | 'chat';
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const store = useNovaStoreContext();
  
  // Measure the panel width and update the store
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    const measureWidth = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      
      // Only update if the width has changed significantly
      if (Math.abs(width - store.panelWidths[panel]) > 5) {
        store.setPanelWidth(panel, width);
      }
    };
    
    // Measure initially
    measureWidth();
    
    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      measureWidth();
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, [store, panel]);
  
  return (
    <div ref={containerRef} className="w-full h-full">
      {children}
    </div>
  );
} 